import { createBrowserRouter, Navigate, redirectDocument } from "react-router-dom";
import LegalUrls from "@/_constants/LegalUrls.ts";
import Routes from "./Routes.ts";
import LoginPage from "@/pages/LoginPage.tsx";
import { transformRouterConfig } from "@/router/transformRouter.tsx";
import RegisterPage from "@/pages/RegisterPage.tsx";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage.tsx";
import ResetPasswordPage from "@/pages/ResetPasswordPage.tsx";
import HomePage from "@/pages/HomePage.tsx";
import SimpleCenter from "@/layouts/SimpleCenter/SimpleCenter.tsx";
import ConfirmEmailPage from "@/pages/ConfirmEmailPage.tsx";
import PaymentSelectPage from "@/pages/PaymentSelectPage.tsx";
import { LayoutInternal } from "$/views/emails/layout.tsx";
import BrandedSimple from "@/layouts/BrandedSimple/BrandedSimple.tsx";
import PasswordChangedConfirmationPage from "@/pages/PasswordChangedConfirmationPage.tsx";
import EmailChangedConfirmationPage from "@/pages/EmailChangedConfirmationPage.tsx";
import OnboardingAskNamePage from "@/pages/Onboarding/OnboardingAskNamePage.tsx";
import OnboardingStartPage from "@/pages/Onboarding/OnboardingStartPage.tsx";
import OnboardingTravelDnaPage from "@/pages/Onboarding/OnboardingTravelDnaPage.tsx";
import OnboardingDnaConfirmationPage from "@/pages/Onboarding/OnboardingDnaConfirmationPage.tsx";
import OnboardingTripCreatePage from "@/pages/Onboarding/OnboardingTripCreatePage.tsx";
import TripConfirmationPage from "@/pages/Trip/TripConfirmationPage.tsx";
import TripEditPage from "@/pages/Trip/TripEditPage.tsx";
import TripLayout from "@/layouts/TripLayout/TripLayout.tsx";
import TripPage from "@/pages/Trip/TripPage.tsx";
import SettingsPage from "@/pages/SettingsPage.tsx";
import TrialStartPage from "@/pages/Subscription/TrialStartPage.tsx";
import EarlyAccessPage from "@/pages/Subscription/EarlyAccessPage.tsx";
import SubscriptionSelectPage from "@/pages/Subscription/SubscriptionSelectPage.tsx";
import AfterPaymentLandingPage from "@/pages/AfterPaymentLandingPage.tsx";
import UpgradePage from "@/pages/Subscription/UpgradePage.tsx";
import RegistrationFlow from "@/context/RegistrationFlow.tsx";
import OnboardingWelcomePage from "@/pages/Onboarding/OnboardingWelcomePage.tsx";
import ProfilePage from "@/pages/ProfilePage.tsx";

const router = createBrowserRouter([
  // External redirects bypass the middleware transformer, which only preserves elements and paths.
  {
    path: Routes.POLICY,
    loader: () => redirectDocument(LegalUrls.PRIVACY),
  },
  {
    path: Routes.TERMS,
    loader: () => redirectDocument(LegalUrls.TERMS),
  },
  ...transformRouterConfig([
    // Trip layout
    {
      path: "/",
      element: <TripLayout.Layout />,
      middleware: ["loggedIn", "onboardingFinished"],
      children: [
        {
          path: Routes.DASHBOARD,
          element: <HomePage />,
        },
        {
          path: Routes.TRIP,
          element: <TripPage />,
        },
        {
          path: Routes.PROFILE,
          element: <ProfilePage />,
        },
        {
          path: Routes.SETTINGS,
          element: <SettingsPage />,
        },
        {
          path: Routes.DNA_EDIT,
          element: <OnboardingTravelDnaPage />,
        },
      ],
    },
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["notLoggedIn"],
      children: [
        {
          path: Routes.FORGOT_PASSWORD,
          element: <ForgotPasswordPage />,
        },
        {
          path: Routes.LOGIN,
          element: <LoginPage />,
        },
      ],
    },
    {
      path: "/",
      element: (
        <RegistrationFlow>
          <SimpleCenter />
        </RegistrationFlow>
      ),
      middleware: ["notLoggedIn"],
      children: [
        {
          path: Routes.REGISTER,
          element: <RegisterPage />,
        },
        {
          path: Routes.CONFIRM_EMAIL,
          element: <ConfirmEmailPage />,
        },
      ],
    },
    // SimpleCenter logged in onboarding not finished (ONBOARDING)
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["loggedIn", "onboardingNotFinished"],
      children: [
        {
          path: Routes.PREFERRED_NAME,
          element: <OnboardingAskNamePage />,
        },
        {
          path: Routes.ONBOARDING__START,
          element: <OnboardingStartPage />,
        },
        {
          path: Routes.ONBOARDING__WELCOME,
          element: <OnboardingWelcomePage />,
        },
        {
          path: Routes.ONBOARDING__DNA,
          element: <OnboardingTravelDnaPage />,
        },
        {
          path: Routes.ONBOARDING__DNA_CONFIRMATION,
          element: <OnboardingDnaConfirmationPage />,
        },
        {
          path: "/",
          middleware: ["dnaCreated"],
          children: [
            {
              path: Routes.ONBOARDING__TRIP_CREATE,
              element: <OnboardingTripCreatePage />,
            },
          ],
        },
      ],
    },
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["loggedIn", "onboardingTripReady"],
      children: [
        {
          path: Routes.TRIP__CONFIRM,
          element: <TripConfirmationPage />,
        },
      ],
    },
    // Simple Center onboarding finished
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["loggedIn", "onboardingFinished"],
      children: [
        {
          path: Routes.TRIP__EDIT,
          element: <TripEditPage />,
        },
      ],
    },
    // Simple Center onboarding finished no active subscription
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["loggedIn", "onboardingFinished", "hasNotSubscription"],
      children: [
        {
          path: Routes.TRIAL,
          element: import.meta.env.VITE_EARLY_ACCESS_ENABLED === "true"
            ? <EarlyAccessPage />
            : <TrialStartPage />,
        },
      ],
    },
    // Simple Center onboarding finished is trial
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: [
        "loggedIn",
        "onboardingFinished",
        "isTrialOrNotActiveSubscription",
      ],
      children: [
        {
          path: Routes.SUBSCRIBE,
          element: <SubscriptionSelectPage />,
        },
      ],
    },
    // Simple Center onboarding finished can upgrade
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["loggedIn", "onboardingFinished", "canUpgrade"],
      children: [
        {
          path: Routes.UPGRADE,
          element: <UpgradePage />,
        },
      ],
    },
    // SimpleCenter layout
    {
      path: "/",
      element: <SimpleCenter />,
      children: [
        {
          path: Routes.RESET_PASSWORD,
          element: <ResetPasswordPage />,
        },
      ],
    },
    {
      path: "/",
      element: <BrandedSimple />,
      children: [
        {
          path: Routes.PASSWORD_CHANGED_CONFIRMATION,
          element: <PasswordChangedConfirmationPage />,
        },
        {
          path: Routes.EMAIL_CHANGED_CONFIRMATION,
          element: <EmailChangedConfirmationPage />,
        },
      ],
    },
    // SimpleCenter logged in
    {
      path: "/",
      element: <SimpleCenter />,
      middleware: ["loggedIn"],
      children: [
        {
          path: Routes.PAYMENT_SELECT,
          element: <PaymentSelectPage />,
        },
        {
          path: Routes.AFTER_PAYMENT_LANDING,
          element: <AfterPaymentLandingPage />,
        },
      ],
    },
    {
      path: Routes.EMAIL_TEMPLATE,
      element: (
        <LayoutInternal
          appUrl={import.meta.env.VITE_APP_URL}
          appName={"journi"}
          logo={`${import.meta.env.VITE_APP_URL}/assets/logo.png`}
        >
          Lorem ipsum dolor sit amet, consectetur adipisicing elit. Ab animi
          atque possimus, quaerat quasi tempore totam! Architecto assumenda
          cumque deleniti dolor expedita facilis illo minima nihil quidem
          soluta? Hic, laboriosam!
        </LayoutInternal>
      ),
    },
    {
      path: "*",
      element: <Navigate to={Routes.DASHBOARD} />,
    },
  ]),
]);

export default router;
